// ============================================
// P1.5 G-03：态 B「组件选择大面板」（点击大类后弹出；搜索+子类 chips+预览卡片网格）
// - 拖拽（react-dnd，item 契约同现网）或点击添加到画布中心
// - 拖拽中面板避让（pointer-events:none + 半透明），不遮挡画布投放
// - Esc / ✕ 关闭；跨大类搜索（态 A 搜索触发）
// ============================================
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDrag } from 'react-dnd';
import { Button, Input, message, Tag } from 'antd';
import { CloseOutlined, SearchOutlined, LockOutlined } from '@ant-design/icons';
import { widgetRegistry } from '../../registry/widgetRegistry';
import {
  resolveWidgetGroup,
} from '../../registry/widgetGroups';
import useEditorUiStore from '../../stores/editorUiStore';
import useDashboardStore from '../../stores/dashboardStore';
import usePluginStore from '../../plugins/plugin-manager';
import { usePayIntentStore } from '../payment/payIntent';
import WidgetPreviewCard from './WidgetPreviewCard';
import { widgetIcon } from '../../registry/widgetIcons';
import { readRecentTypes, pushRecentType, moveFocus, computeColumns } from './widgetPickerUtils';
import type { WidgetDefinition, VisualComponent } from '@dtwin/shared';

const PANEL_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 64,
  width: 900,
  maxWidth: 'calc(100vw - 300px)',
  height: 'min(620px, calc(100vh - 110px))',
  background: '#16162a',
  border: '1px solid #2d2d47',
  borderRadius: 8,
  boxShadow: '0 12px 40px rgba(0,0,0,0.55)',
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

/** 点击添加：落在画布中心（与拖拽落点逻辑一致，付费/积分锁定兜底） */
function addWidgetAtCenter(def: WidgetDefinition): void {
  const market = usePluginStore.getState().marketComponents.find((c) => c.type === def.type);
  if (market?.entitled === false && market.priceType === 'credits') {
    // P2-4：积分组件余额不足 → 锁定并提示（服务端按次扣减，充值后刷新即可）
    message.warning(`「${market.name}」需要 ${Number(market.creditPrice) || 0} 积分/次，当前积分不足，请联系管理员充值后再使用`);
    return;
  }
  if (market?.priceType === 'paid' && market.entitled === false) {
    // P3 支付线：有单购价格 → 弹出支付；否则提示开通套餐
    if (market.price && Number(market.price) > 0) {
      usePayIntentStore.getState().openPay(
        { orderType: 'plugin_component', productRef: def.type, amount: Number(market.price), subject: `付费组件-${market.name}` },
        () => { void usePluginStore.getState().syncComponentMarket?.(); },
      );
      message.info(`请在支付窗口完成「${market.name}」购买`);
    } else {
      message.warning(`「${market.name}」为付费组件，请开通会员 / 订阅对应套餐后使用`);
    }
    return;
  }
  const store = useDashboardStore.getState();
  const page = store.currentPage;
  if (!page) {
    message.warning('请先打开大屏页面');
    return;
  }
  const pageW = Number((page.config as any)?.width) || 1920;
  const pageH = Number((page.config as any)?.height) || 1080;
  const size = def.defaultSize || { width: 200, height: 100 };
  const before = store.components.map((c) => c.id);
  store.addComponent({
    type: def.type as VisualComponent['type'],
    position: {
      x: Math.max(0, Math.round((pageW - size.width) / 2)),
      y: Math.max(0, Math.round((pageH - size.height) / 2)),
    },
    size,
    style: {},
    config: {},
  });
  // 2026-10-05：添加后自动选中（右侧属性区随即可编辑）；scene 就地编辑态关闭选择面板避免遮挡画布
  const created = useDashboardStore.getState().components.find((c) => !before.includes(c.id));
  if (created) useDashboardStore.getState().selectComponent(created.id);
  pushRecentType(def.type);
  if (useEditorUiStore.getState().activeEditor === 'scene') {
    useEditorUiStore.getState().closeWidgetPicker();
  }
  message.success(`已添加「${def.name}」到画布中心`);
}

interface PickerCardProps {
  def: WidgetDefinition;
  locked: boolean;
  focused?: boolean;
}

function PickerCard({ def, locked, focused }: PickerCardProps) {
  const setPanelDragging = useEditorUiStore((s) => s.setPanelDragging);
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'VISUAL_COMPONENT',
      item: { type: def.type, size: def.defaultSize },
      canDrag: () => !locked,
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [def.type, def.defaultSize, locked],
  );

  useEffect(() => {
    if (isDragging) {
      setPanelDragging(true);
      pushRecentType(def.type);
    }
    return () => setPanelDragging(false);
  }, [isDragging, setPanelDragging, def.type]);

  return (
    <div
      ref={drag as unknown as React.Ref<HTMLDivElement>}
      data-testid={`widget-card-${def.type}`}
      data-locked={locked ? 'true' : 'false'}
      onClick={() => {
        // P3 支付线（M-02）：锁定/付费逻辑统一在 addWidgetAtCenter 内处理
        // （付费且有单价 → 弹出支付弹窗；付费但无单价 → 提示开通会员/套餐）
        addWidgetAtCenter(def);
      }}
      title="拖拽到画布，或点击添加到画布中心"
      style={{
        width: 176,
        padding: 8,
        borderRadius: 6,
        border: focused ? '1px solid #3b82f6' : '1px solid #2d2d47',
        boxShadow: focused ? '0 0 0 2px rgba(59,130,246,0.35)' : undefined,
        background: 'rgba(255,255,255,0.02)',
        cursor: locked ? 'not-allowed' : 'grab',
        opacity: isDragging ? 0.5 : locked ? 0.55 : 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        userSelect: 'none',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = '#3b82f6';
        (e.currentTarget as HTMLElement).style.background = 'rgba(59,130,246,0.08)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.borderColor = '#2d2d47';
        (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)';
      }}
    >
      <WidgetPreviewCard def={def} width={158} height={92} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: '#8b8ba7', fontSize: 13 }}>{widgetIcon(def.type)}</span>
        <span style={{ color: locked ? '#8b8ba7' : '#ddd', fontSize: 12, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {def.name}
        </span>
        {locked && (
          <Tag color="warning" style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
            <LockOutlined /> 付费
          </Tag>
        )}
      </div>
    </div>
  );
}

export default function WidgetPickerPanel() {
  const open = useEditorUiStore((s) => s.widgetPickerOpen);
  const group = useEditorUiStore((s) => s.widgetPickerGroup);
  const subGroup = useEditorUiStore((s) => s.widgetPickerSubGroup);
  const dragging = useEditorUiStore((s) => s.panelDragging);
  const close = useEditorUiStore((s) => s.closeWidgetPicker);
  const setGroup = useEditorUiStore((s) => s.setWidgetPickerGroup);
  const setSubGroup = useEditorUiStore((s) => s.setWidgetPickerSubGroup);
  const setGlobalSearch = useEditorUiStore((s) => s.setWidgetPickerSearch);
  const [localSearch, setLocalSearch] = useState('');
  const [version, setVersion] = useState(0);

  useEffect(() => widgetRegistry.subscribe(() => setVersion((v) => v + 1)), []);
  useEffect(() => {
    if (open) {
      // 打开时带走态 A 的搜索词（跨大类搜索入口）
      setLocalSearch(useEditorUiStore.getState().widgetPickerSearch);
    }
  }, [open]);

  // Esc 关闭
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  // 拖拽避让（原生兜底）：任何 dragstart 期间面板 pointer-events 关闭，确保画布可接收投放
  useEffect(() => {
    if (!open) return;
    const setDrag = useEditorUiStore.getState().setPanelDragging;
    const onStart = () => setDrag(true);
    const onEnd = () => setDrag(false);
    document.addEventListener('dragstart', onStart);
    document.addEventListener('dragend', onEnd);
    document.addEventListener('drop', onEnd);
    return () => {
      document.removeEventListener('dragstart', onStart);
      document.removeEventListener('dragend', onEnd);
      document.removeEventListener('drop', onEnd);
      setDrag(false);
    };
  }, [open]);

  const all = useMemo(() => widgetRegistry.getAll(), [version]);
  const marketComponents = usePluginStore((s) => s.marketComponents);
  // P1（2026-10-16）：有效分组（内置 + 分组配置 + 市场声明合并）
  const effectiveGroups = usePluginStore((s) => s.effectiveGroups);
  const isLocked = (type: string) => {
    const m = marketComponents.find((c) => c.type === type);
    return m?.priceType === 'paid' && m.entitled === false;
  };

  // ── G-05：列表与键盘导航状态（hooks 需在 early return 之前）──
  const search = localSearch.trim().toLowerCase();
  const crossGroup = search.length > 0;
  let items = group === 'all' ? all : all.filter((r) => (r.def.group || resolveWidgetGroup(r.def.type).group) === group);
  if (crossGroup) {
    items = all.filter(
      (r) => r.def.name.toLowerCase().includes(search) || r.def.type.toLowerCase().includes(search),
    );
  } else if (subGroup) {
    items = items.filter((r) => r.def.subGroup === subGroup);
  }
  const subGroups = effectiveGroups.subGroups[group] || [];
  const title = crossGroup ? `搜索「${localSearch}」` : (effectiveGroups.groups.find((g) => g.key === group)?.label || group);
  // 同子类内按 order 排序（缺省保持注册顺序）
  items = items
    .slice()
    .sort((a, b) => ((a.def.order ?? Number.MAX_SAFE_INTEGER) - (b.def.order ?? Number.MAX_SAFE_INTEGER)));
  // 最近使用（仅「全部」大类且未筛子类/未搜索时置顶）
  if (!crossGroup && !subGroup && group === 'all') {
    const recent = readRecentTypes();
    if (recent.length) {
      items = items
        .slice()
        .sort((a, b) => {
          const ia = recent.indexOf(a.def.type);
          const ib = recent.indexOf(b.def.type);
          if (ia === -1 && ib === -1) return 0;
          if (ia === -1) return 1;
          if (ib === -1) return -1;
          return ia - ib;
        });
    }
  }

  const [focusIndex, setFocusIndex] = useState(0);
  const gridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setFocusIndex(0);
  }, [group, subGroup, localSearch]);

  // 键盘导航：←→ ±1，↑↓ ±列数，Enter 添加（输入框内不拦截）
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      if (items.length === 0) return;
      const columns = computeColumns(gridRef.current?.clientWidth || 0);
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setFocusIndex((i) => moveFocus(i, 1, items.length));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setFocusIndex((i) => moveFocus(i, -1, items.length));
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusIndex((i) => moveFocus(i, columns, items.length));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusIndex((i) => moveFocus(i, -columns, items.length));
      } else if (e.key === 'Enter') {
        const def = items[Math.min(focusIndex, items.length - 1)]?.def;
        if (!def) return;
        e.preventDefault();
      if (isLocked(def.type)) {
        // P3 支付线：付费未授权（购买或提示逻辑在 addWidgetAtCenter 内统一处理）
        addWidgetAtCenter(def);
        return;
      }
      addWidgetAtCenter(def);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, items, focusIndex]);

  // G-05：点击画布空白/面板外关闭（忽略面板与左侧大类行，避免与切换大类冲突）
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el) return;
      if (el.closest('[data-testid="widget-picker-panel"]')) return;
      if (el.closest('[data-testid^="component-group-"]')) return;
      if (el.closest('[data-testid="component-panel-search"]')) return;
      useEditorUiStore.getState().closeWidgetPicker();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!open) return null;

  const node = (
    <div
      style={{ ...PANEL_STYLE, left: 260, opacity: dragging ? 0.35 : 1 }}
      data-testid="widget-picker-panel"
      data-dragging={dragging ? '1' : '0'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid #2d2d47' }}>
        <span style={{ color: '#e6e6f0', fontSize: 14, fontWeight: 600 }}>
          {title}
          <span style={{ color: '#8b8ba7', fontWeight: 400, marginLeft: 6 }}>({items.length})</span>
        </span>
        <Input
          size="small"
          allowClear
          prefix={<SearchOutlined style={{ color: '#666' }} />}
          placeholder="搜索组件（跨大类）"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          style={{ width: 260, marginLeft: 'auto', background: '#101020' }}
          data-testid="widget-picker-search"
        />
        <Button size="small" type="text" icon={<CloseOutlined />} onClick={close} data-testid="widget-picker-close" />
      </div>

      {!crossGroup && subGroups.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 14px', borderBottom: '1px solid #22223a' }}>
          <Tag.CheckableTag checked={!subGroup} onChange={() => setSubGroup('')}>
            全部
          </Tag.CheckableTag>
          {subGroups.map((s) => (
            <Tag.CheckableTag key={s.key} checked={subGroup === s.key} onChange={() => setSubGroup(s.key)}>
              {s.label}
            </Tag.CheckableTag>
          ))}
        </div>
      )}

      <div
        ref={gridRef}
        style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexWrap: 'wrap', gap: 12, alignContent: 'flex-start' }}
      >
        {items.map(({ def }, i) => (
          <PickerCard key={def.type} def={def} locked={isLocked(def.type)} focused={i === focusIndex} />
        ))}
        {items.length === 0 && (
          <div style={{ color: '#666', fontSize: 13, margin: '40px auto' }}>
            {group === 'extension' && !crossGroup ? '暂无市场组件（后台发布上架后自动出现）' : '无匹配组件'}
          </div>
        )}
      </div>

      {!crossGroup && (group === '3d' || group === 'topology' || group === 'suite') && (
        <div style={{ padding: 8, textAlign: 'center', color: '#666', fontSize: 12, borderTop: '1px solid #22223a' }}>
          该分类内容后续接入（场景模板 / 拓扑组件 / 行业套件）
        </div>
      )}
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(node, document.body) : node;
}

export { addWidgetAtCenter };
